import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, ChevronDown, ChevronRight, Info, AlertTriangle, Filter, X, Pill } from 'lucide-react';
import { cn } from '@/lib/utils';
import api from '@/lib/axios';

// ── Types ─────────────────────────────────────────────────────────────────────

interface Presentacion {
  id_presentacion: string;
  forma_farmaceutica: string;
  via_administracion: string;
  concentracion_valor: number;
  concentracion_unidad: string;
  empaque_tipo: string;
  unidades_por_empaque: number;
  es_pediatrico: boolean;
}

interface Interaccion {
  principio_activo: string;
  severidad: 'CRITICA' | 'ALTA' | 'MODERADA' | 'LEVE';
}

interface Posologia {
  adulto_diaria_max_mg: number | null;
  intervalo_min_horas: number | null;
  intervalo_rec_horas: number | null;
  peds_requiere_peso: boolean;
  peds_dosis_mg_kg: number | null;
  peds_diaria_max_mg_kg: number | null;
}

interface Medicamento {
  id: string;
  nombre_generico: string;
  grupo_fuente: string;
  nombres_comerciales: string[];
  requiere_receta: boolean;
  presentaciones: Presentacion[];
  posologia_general: Posologia | null;
  interacciones_clave: Interaccion[];
  origenClinicaId?: string | null;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const SEVERIDAD_COLOR: Record<string, string> = {
  CRITICA: 'text-red-700 bg-red-50 border-red-200',
  ALTA:    'text-orange-700 bg-orange-50 border-orange-200',
  MODERADA:'text-amber-700 bg-amber-50 border-amber-200',
  LEVE:    'text-blue-700 bg-blue-50 border-blue-200',
};

const SEVERIDAD_BADGE: Record<string, string> = {
  CRITICA: 'bg-red-100 text-red-700',
  ALTA:    'bg-orange-100 text-orange-700',
  MODERADA:'bg-amber-100 text-amber-700',
  LEVE:    'bg-blue-100 text-blue-700',
};

// ── MedicamentoCard ───────────────────────────────────────────────────────────

function MedicamentoCard({ med }: { med: Medicamento }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={cn('border rounded-xl overflow-hidden transition-all', expanded ? 'border-primary-200' : 'border-gray-100')}>
      <button
        type="button"
        onClick={() => setExpanded(p => !p)}
        className={cn('flex items-center justify-between w-full px-4 py-3 text-left transition-colors',
          expanded ? 'bg-primary-50' : 'bg-white hover:bg-gray-50')}
      >
        <div className="flex items-center gap-3 min-w-0">
          <Pill size={14} className="text-primary-400 shrink-0" />
          <div className="min-w-0">
            <span className="font-medium text-sm text-text-primary">{med.nombre_generico}</span>
            {med.nombres_comerciales.length > 0 && (
              <span className="text-[11px] text-text-secondary ml-2">
                ({med.nombres_comerciales.slice(0, 3).join(', ')}{med.nombres_comerciales.length > 3 ? ` +${med.nombres_comerciales.length - 3}` : ''})
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0 ml-2">
          {med.origenClinicaId && (
            <span className="text-[10px] bg-teal-50 text-teal-700 border border-teal-200 px-2 py-0.5 rounded-full font-medium">Agregado por una clínica</span>
          )}
          {med.requiere_receta && (
            <span className="text-[10px] bg-amber-50 text-amber-600 border border-amber-200 px-2 py-0.5 rounded-full font-medium">Receta</span>
          )}
          {med.presentaciones.some(p => p.es_pediatrico) && (
            <span className="text-[10px] bg-blue-50 text-blue-600 border border-blue-200 px-2 py-0.5 rounded-full font-medium">Pediátrico</span>
          )}
          {expanded ? <ChevronDown size={14} className="text-text-secondary" /> : <ChevronRight size={14} className="text-text-secondary" />}
        </div>
      </button>

      {expanded && (
        <div className="px-4 py-3 bg-white border-t border-gray-100 space-y-4">
          {/* Nombres comerciales */}
          {med.nombres_comerciales.length > 0 && (
            <div>
              <p className="text-[11px] font-semibold text-text-secondary uppercase tracking-wide mb-1.5">Nombres comerciales</p>
              <div className="flex flex-wrap gap-1.5">
                {med.nombres_comerciales.map(nc => (
                  <span key={nc} className="text-xs bg-gray-100 text-text-secondary px-2 py-0.5 rounded-full">{nc}</span>
                ))}
              </div>
            </div>
          )}

          {/* Presentaciones */}
          <div>
            <p className="text-[11px] font-semibold text-text-secondary uppercase tracking-wide mb-1.5">Presentaciones</p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 text-text-secondary">
                    <th className="text-left px-2 py-1.5 rounded-l font-medium">Forma</th>
                    <th className="text-left px-2 py-1.5 font-medium">Concentración</th>
                    <th className="text-left px-2 py-1.5 font-medium">Vía</th>
                    <th className="text-left px-2 py-1.5 font-medium">Empaque</th>
                    <th className="text-left px-2 py-1.5 rounded-r font-medium">Pediátrico</th>
                  </tr>
                </thead>
                <tbody>
                  {med.presentaciones.map(p => (
                    <tr key={p.id_presentacion} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="px-2 py-1.5 font-medium capitalize">{p.forma_farmaceutica.charAt(0) + p.forma_farmaceutica.slice(1).toLowerCase()}</td>
                      <td className="px-2 py-1.5">{p.concentracion_valor} {p.concentracion_unidad}</td>
                      <td className="px-2 py-1.5 capitalize">{p.via_administracion.toLowerCase()}</td>
                      <td className="px-2 py-1.5">{p.empaque_tipo} × {p.unidades_por_empaque}</td>
                      <td className="px-2 py-1.5">
                        {p.es_pediatrico
                          ? <span className="text-[10px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded-full">Sí</span>
                          : <span className="text-[10px] text-text-secondary">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Posología */}
          {med.posologia_general && (
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-blue-800 mb-2">
                <Info size={12} /> Posología general
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-blue-900">
                {med.posologia_general.intervalo_rec_horas != null && (
                  <span>Frecuencia recomendada: cada <strong>{med.posologia_general.intervalo_rec_horas} h</strong></span>
                )}
                {med.posologia_general.intervalo_min_horas != null && (
                  <span>Intervalo mínimo: {med.posologia_general.intervalo_min_horas} h</span>
                )}
                {med.posologia_general.adulto_diaria_max_mg != null && (
                  <span>Dosis adulto máx/día: <strong>{med.posologia_general.adulto_diaria_max_mg} mg</strong></span>
                )}
                {med.posologia_general.peds_dosis_mg_kg != null && (
                  <span>Peds: {med.posologia_general.peds_dosis_mg_kg} mg/kg</span>
                )}
                {med.posologia_general.peds_diaria_max_mg_kg != null && (
                  <span>Peds máx/día: {med.posologia_general.peds_diaria_max_mg_kg} mg/kg</span>
                )}
              </div>
            </div>
          )}

          {/* Interacciones */}
          {med.interacciones_clave.length > 0 && (
            <div>
              <p className="text-[11px] font-semibold text-text-secondary uppercase tracking-wide mb-1.5">
                Interacciones clave ({med.interacciones_clave.length})
              </p>
              <div className="space-y-1">
                {med.interacciones_clave.map((i, idx) => (
                  <div key={idx} className={cn('flex items-center gap-2 px-2 py-1.5 rounded border text-xs font-medium', SEVERIDAD_COLOR[i.severidad])}>
                    <AlertTriangle size={11} className="shrink-0" />
                    <span>{i.principio_activo}</span>
                    <span className={cn('ml-auto text-[10px] px-1.5 py-0.5 rounded-full font-semibold', SEVERIDAD_BADGE[i.severidad])}>
                      {i.severidad}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function VademecumGeneralPage() {
  const [q, setQ] = useState('');
  const [grupoFiltro, setGrupoFiltro] = useState('');
  const [recetaFiltro, setRecetaFiltro] = useState<'' | 'true' | 'false'>('');
  const [pediatricoFiltro, setPediatricoFiltro] = useState<'' | 'true' | 'false'>('');
  const [expandedGrupos, setExpandedGrupos] = useState<Set<string>>(new Set());

  const { data: grupos = [] } = useQuery<string[]>({
    queryKey: ['vademecum-grupos'],
    queryFn: () => api.get('/api/vademecum/grupos').then(r => r.data),
    staleTime: Infinity,
  });

  const { data: medicamentos = [], isFetching } = useQuery<Medicamento[]>({
    queryKey: ['vademecum-general', q, grupoFiltro, recetaFiltro, pediatricoFiltro],
    queryFn: () => api.get('/api/vademecum', {
      params: {
        all: 'true',
        ...(q ? { q } : {}),
        ...(grupoFiltro ? { grupo: grupoFiltro } : {}),
        ...(recetaFiltro !== '' ? { requiereReceta: recetaFiltro } : {}),
        ...(pediatricoFiltro !== '' ? { pediatrico: pediatricoFiltro } : {}),
      },
    }).then(r => r.data),
    staleTime: 120_000,
  });

  // Group by grupo_fuente sorted alphabetically within each group
  const byGrupo = medicamentos.reduce<Record<string, Medicamento[]>>((acc, m) => {
    (acc[m.grupo_fuente] ??= []).push(m);
    return acc;
  }, {});

  const totalActivos = medicamentos.length;
  const hayFiltros = q || grupoFiltro || recetaFiltro || pediatricoFiltro;

  function toggleGrupo(g: string) {
    setExpandedGrupos(prev => {
      const next = new Set(prev);
      if (next.has(g)) next.delete(g); else next.add(g);
      return next;
    });
  }

  function clearFiltros() {
    setQ('');
    setGrupoFiltro('');
    setRecetaFiltro('');
    setPediatricoFiltro('');
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-4">
      {/* Header */}
      <div>
        <h1 className="text-xl font-bold text-text-primary">Vademecum General</h1>
        <p className="text-sm text-text-secondary mt-1">
          {totalActivos.toLocaleString()} medicamentos disponibles · Solo lectura
        </p>
      </div>

      {/* Filtros */}
      <div className="bg-white border border-gray-100 rounded-xl p-4 space-y-3">
        <div className="flex items-center gap-3 flex-wrap">
          {/* Buscador */}
          <div className="relative flex-1 min-w-[200px]">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary pointer-events-none" />
            <input
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Buscar por nombre genérico o comercial..."
              className="w-full pl-8 pr-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
            />
          </div>

          {/* Grupo */}
          <select
            value={grupoFiltro}
            onChange={e => setGrupoFiltro(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white"
          >
            <option value="">Todos los grupos</option>
            {grupos.map(g => <option key={g} value={g}>{g}</option>)}
          </select>

          {/* Requiere receta */}
          <select
            value={recetaFiltro}
            onChange={e => setRecetaFiltro(e.target.value as '' | 'true' | 'false')}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white"
          >
            <option value="">Todos (receta)</option>
            <option value="true">Requiere receta</option>
            <option value="false">Sin receta</option>
          </select>

          {/* Pediátrico */}
          <select
            value={pediatricoFiltro}
            onChange={e => setPediatricoFiltro(e.target.value as '' | 'true' | 'false')}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white"
          >
            <option value="">Todos (pediátrico)</option>
            <option value="true">Solo pediátrico</option>
            <option value="false">No pediátrico</option>
          </select>

          {hayFiltros && (
            <button type="button" onClick={clearFiltros}
              className="flex items-center gap-1 text-xs text-text-secondary hover:text-text-primary px-2 py-1.5 rounded-lg hover:bg-gray-100 transition-colors">
              <X size={12} /> Limpiar
            </button>
          )}
        </div>

        {isFetching && <p className="text-[11px] text-text-secondary">Cargando...</p>}
      </div>

      {/* Resultados agrupados */}
      {Object.keys(byGrupo).length === 0 && !isFetching && (
        <div className="text-center py-12 text-text-secondary">
          <Filter size={32} className="mx-auto mb-3 opacity-40" />
          <p className="text-sm">No se encontraron medicamentos con los filtros actuales</p>
        </div>
      )}

      {Object.entries(byGrupo).map(([grupo, meds]) => (
        <div key={grupo} className="border border-gray-100 rounded-xl overflow-hidden">
          <button
            type="button"
            onClick={() => toggleGrupo(grupo)}
            className="flex items-center justify-between w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
          >
            <div className="flex items-center gap-2">
              {expandedGrupos.has(grupo) ? <ChevronDown size={15} className="text-text-secondary" /> : <ChevronRight size={15} className="text-text-secondary" />}
              <span className="font-semibold text-sm text-text-primary">{grupo}</span>
              <span className="text-[11px] text-text-secondary bg-white border border-gray-200 px-2 py-0.5 rounded-full">{meds.length}</span>
            </div>
          </button>

          {expandedGrupos.has(grupo) && (
            <div className="divide-y divide-gray-50 px-3 pb-3 pt-2 space-y-1.5">
              {meds.map(m => (
                <MedicamentoCard key={m.id} med={m} />
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
